import axios from 'axios';

// Interface definitions
export interface Decision {
    id: string;
    name: string;
    nameSlug?: string;
    description?: string;
    version?: string;
    decisionId?: string;
    createdDate?: string;
    modifiedDate?: string;
    lastModifiedDate?: string;
}

export interface DecisionContent {
    id: string;
    nameSlug: string;
    version: string;
    dmnXml: string; // Base64 encoded
    dmnXmlContentType: string;
    dmn: Decision;
}

export interface DecisionListResponse {
    content: Decision[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

// Get auth token from localStorage
const getAuthToken = (): string => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create API instance
const designerApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/designer/api' : '/designer/api',
});

// Add request interceptor for auth token
designerApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
designerApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Unauthorized - token may have expired');
        }
        return Promise.reject(error);
    }
);

// Create axios instance for Engine REST API (deployments)
const engineApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/engine-rest' : '/engine/engine-rest',
});

// Add auth interceptor to engine API as well
engineApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ============================================================================
// Decision CRUD Operations
// ============================================================================

/**
 * Get paginated list of decisions
 */
export const getDecisions = async (
    page: number = 0,
    size: number = 20,
    search?: string
): Promise<DecisionListResponse> => {
    let url = `/dmns?page=${page}&size=${size}&sort=createdDate,DESC`;
    if (search) {
        url += `&name=${encodeURIComponent(search)}`;
    }
    const response = await designerApi.get<DecisionListResponse>(url);
    return response.data;
};

/**
 * Get decision by ID
 */
export const getDecisionById = async (id: string): Promise<Decision> => {
    const response = await designerApi.get<Decision>(`/dmns/${id}`);
    return response.data;
};

/**
 * Create new decision
 */
export const createDecision = async (decision: Partial<Decision>): Promise<Decision> => {
    const response = await designerApi.post<Decision>('/dmns', decision);
    return response.data;
};

/**
 * Update decision
 */
export const updateDecision = async (id: string, decision: Partial<Decision>): Promise<Decision> => {
    const response = await designerApi.put<Decision>(`/dmns/${id}`, decision);
    return response.data;
};

/**
 * Delete decision
 */
export const deleteDecision = async (id: string): Promise<void> => {
    await designerApi.delete(`/dmns/${id}`);
};

// ============================================================================
// Decision Content Operations
// ============================================================================

/**
 * Get decision content by nameSlug (latest version)
 */
export const getDecisionContent = async (nameSlug: string): Promise<DecisionContent> => {
    const response = await designerApi.get<DecisionContent>(`/dmn-contents/name/${nameSlug}`);
    return response.data;
};

/**
 * Get decision content by nameSlug and version
 */
export const getDecisionContentByVersion = async (
    nameSlug: string,
    version: string
): Promise<DecisionContent> => {
    const response = await designerApi.get<DecisionContent>(
        `/dmn-contents/name/${nameSlug}/version/${version}`
    );
    return response.data;
};

/**
 * Get all versions for a decision
 */
export const getDecisionVersions = async (nameSlug: string): Promise<DecisionContent[]> => {
    const response = await designerApi.get<DecisionContent[]>(`/dmn-contents/versions/${nameSlug}`);
    return response.data;
};

/**
 * Save decision content (DMN XML)
 */
export const saveDecisionContent = async (
    nameSlug: string,
    dmnXml: string,
    version: string
): Promise<DecisionContent> => {
    const decisionContent = {
        nameSlug,
        version,
        dmnXml: btoa(dmnXml), // Base64 encode
        dmnXmlContentType: 'text/xml',
    };
    const response = await designerApi.post<DecisionContent>('/dmn-contents', decisionContent);
    return response.data;
};

// ============================================================================
// DMN XML Utilities
// ============================================================================

/**
 * Decode base64 DMN XML
 */
export const decodeDmnXML = (base64Dmn: string): string => {
    try {
        return atob(base64Dmn);
    } catch (error) {
        console.error('Error decoding DMN XML:', error);
        return '';
    }
};

/**
 * Empty DMN template
 */
export const emptyDmnTemplate = (decisionName: string = 'New Decision'): string => {
    return `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="https://www.omg.org/spec/DMN/20191111/MODEL/" 
             xmlns:dmndi="https://www.omg.org/spec/DMN/20191111/DMNDI/" 
             xmlns:dc="http://www.omg.org/spec/DD/20100524/DC/" 
             xmlns:di="http://www.omg.org/spec/DD/20100524/DI/" 
             xmlns:camunda="http://camunda.org/schema/1.0/dmn"
             id="definitions_${Date.now()}" 
             name="${decisionName}" 
             namespace="http://camunda.org/schema/1.0/dmn">
  <decision id="decision_${Date.now()}" name="${decisionName}">
    <decisionTable id="decisionTable_${Date.now()}">
      <input id="input_1" label="Input">
        <inputExpression id="inputExpression_1" typeRef="string">
          <text></text>
        </inputExpression>
      </input>
      <output id="output_1" label="Output" name="result" typeRef="string" />
    </decisionTable>
  </decision>
</definitions>`;
};

/**
 * Deploy decision to Camunda engine
 */
export const deployDecision = async (
    nameSlug: string,
    dmnXml: string,
    decisionName: string
): Promise<any> => {
    const formData = new FormData();
    const blob = new Blob([dmnXml], { type: 'text/xml' });
    formData.append('deployment-name', `${decisionName} Deployment`);
    formData.append('deploy-changed-only', 'true');
    formData.append('deployment-source', 'doit-studio');
    formData.append('data', blob, `${nameSlug}.dmn`);

    const response = await engineApi.post('/deployment/create', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};
